import DownloadIcon from "@mui/icons-material/SaveAltRounded";
import { Button, Stack, Typography } from "@mui/material";
import { useState } from "react";
import type { HandledApiResponse } from "@entrilia/shared/api/axiosHelper";
import type { FileDownloadInfo } from "@entrilia/shared/api/fileTypes";
import FileIcon from "@entrilia/shared/components/FileIcon";
import PreviewFileDialog from "@entrilia/shared/components/previewFile/PreviewFileDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import { distinct } from "@entrilia/shared/utilities/arrayHelper";
import { getFileExtension } from "@entrilia/shared/utilities/fileHelper";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import VisibleIcon from "@entrilia/shared/icons/VisibleIcon";
import CategoryTag from "../../../../common/CategoryTag";
import GeneralPageHeader from "../../../../common/GeneralPageHeader";

interface PageTitleProps {
  fileName?: string;
  categoryId?: string;
  documentCollectionNo?: number;
  fundNames: string[];
}

const getFundInfo = (fundNames: string[]): string => {
  switch (fundNames.length) {
    case 0:
      return "";
    case 1:
      return defined(fundNames[0]);
    case 2:
      return `${fundNames[0]}, ${fundNames[1]}`;
    default:
      return `${fundNames[0]} and ${fundNames.length - 1} more funds`;
  }
};

const PageTitle = ({ fileName, categoryId, documentCollectionNo, fundNames }: PageTitleProps) => {
  const fundInfo = getFundInfo(distinct([...fundNames].sort()));

  return (
    <Stack spacing={0.5} sx={{ py: 1.5 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <FileIcon fileExtension={getFileExtension(fileName ?? "")} />
        {fileName && (
          <Typography variant="h6" noWrap sx={{ maxWidth: "40rem" }}>
            {fileName}
          </Typography>
        )}
        {!fileName && (
          <Typography noWrap color="error">
            The document has been removed
          </Typography>
        )}
        <CategoryTag externalCategoryId={categoryId} />
      </Stack>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Typography variant="caption">Document Activity</Typography>
        {fundInfo && <Typography variant="caption">&bull;</Typography>}
        {fundInfo && <Typography variant="caption">{fundInfo}</Typography>}
        {documentCollectionNo !== undefined && (
          <>
            <Typography variant="caption">&bull;</Typography>
            <Typography variant="caption" color="textSecondary">
              Collection ID:
            </Typography>
            {fundInfo && <Typography variant="caption">{documentCollectionNo.toLocaleString()}</Typography>}
          </>
        )}
      </Stack>
    </Stack>
  );
};

interface Props {
  backPath: string;
  fileName?: string;
  categoryId?: string;
  documentCollectionNo?: number;
  fundNames: string[];
  getFileDownloadInfo: () => Promise<HandledApiResponse<FileDownloadInfo>>;
}

const DocumentActivityPageHeader = ({
  backPath,
  fileName,
  categoryId,
  documentCollectionNo,
  fundNames,
  getFileDownloadInfo,
}: Props) => {
  const { sendNotificationError } = useNotificationContext();

  const [documentPreviewState, setDocumentPreviewState] = useState<FileDownloadInfo>();

  const handleViewDocument = async () => {
    const [downloadInfo, error] = await getFileDownloadInfo();
    if (error) {
      logError(error, "[DocumentActivityPageHeader] getFileDownloadInfo");
      sendNotificationError(error.message);
      return;
    }

    setDocumentPreviewState(downloadInfo);
  };

  const handleDownloadDocument = async () => {
    const [downloadInfo, error] = await getFileDownloadInfo();
    if (error) {
      logError(error, "[DocumentActivityPageHeader] getFileDownloadInfo");
      sendNotificationError(error.message);
      return;
    }

    await downloadFileFromUrl(downloadInfo.downloadUrl);
  };

  return (
    <>
      <GeneralPageHeader
        TitleComponent={
          <PageTitle
            fileName={fileName}
            categoryId={categoryId}
            documentCollectionNo={documentCollectionNo}
            fundNames={fundNames}
          />
        }
        showDefaultBackButtonTo={backPath}
      >
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" color="secondary" onClick={handleViewDocument} startIcon={<VisibleIcon />}>
            View
          </Button>
          <Button variant="outlined" color="secondary" onClick={handleDownloadDocument} startIcon={<DownloadIcon />}>
            Download
          </Button>
        </Stack>
      </GeneralPageHeader>
      {documentPreviewState && (
        <PreviewFileDialog
          url={documentPreviewState.downloadUrl}
          fileName={documentPreviewState.fileName}
          onClose={() => setDocumentPreviewState(undefined)}
        />
      )}
    </>
  );
};

export default DocumentActivityPageHeader;
